import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { FilterDialogComponent } from 'src/app/features/produits/components/filter-dialog/filter-dialog.component';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { parseAgentFilterRequest, refillAgentFilter } from 'src/app/shared/utils/helpers';
import { IAgentFilter } from '../../models/agents.models';
import { FetchAllCountries } from 'src/app/features/customers/store/customers.actions';
import { ICountry } from 'src/app/features/customers/models/customers.models';
import { MatChipInputEvent } from '@angular/material/chips';

@Component({
  selector: 'app-agents-filter',
  templateUrl: './agents-filter.component.html',
  styleUrls: ['./agents-filter.component.scss'],
})
export class AgentsFilterComponent implements OnInit {
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  countries$: Observable<ICountry[]> = of([]);
  countries: ICountry[] = [];

  tags: string[] = []

  currentFilter: Observable<IAgentFilter> = this.store.select((state) => state.agents.agentFilterState.advanced);
  agentFilters = LocalStorageService.get('agentFilters');

  selectedGroup = 'agent_info';

  filter: IAgentFilter = refillAgentFilter();
  filterName = '';
  toggleSaveFilterForm = false;
  filterNameControl = new FormControl('', Validators.required);
  private subscriptions: Subscription[] = [];
  constructor(
    public dialogRef: MatDialogRef<FilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<AppState>,
  ) {}

  ngOnInit(): void {
    let sub1: Subscription | undefined;
    if (this.data) {
      this.filter = refillAgentFilter();
      this.filter.agentsInfoFilter.first_name = this.data.filter?.first_name ?? undefined;
      this.filter.agentsInfoFilter.last_name = this.data.filter?.last_name ?? undefined;
      this.filter.agentsInfoFilter.email = this.data.filter?.email ?? undefined;
      this.filter.agentsInfoFilter.phone = this.data.filter?.phone ?? undefined;
      this.filter.agentsInfoFilter.country = this.data.filter?.country ?? undefined;
      this.filter.agentsInfoFilter.address = this.data.filter?.address ?? undefined;
      this.filter.agentsInfoFilter.vat = this.data.filter?.vat ?? undefined;
      this.filter.agentsInfoFilter.billing_address = this.data.filter?.billing_address ?? undefined;
      this.filter.extraInfoFilter.catalog_name = this.data.filter?.catalog_name ?? undefined;
      this.filter.extraInfoFilter.tags = this.data.filter?.tags ?? undefined;
      this.filter.extraInfoFilter.location_name = this.data.filter?.location_name ?? undefined;
      this.filter.extraInfoFilter.nbr_orders_min = this.data.filter?.nbr_orders_min ?? undefined;
      this.filter.extraInfoFilter.nbr_orders_max = this.data.filter?.nbr_orders_max ?? undefined;
      this.filter.extraInfoFilter.nbr_locations_min = this.data.filter?.nbr_locations_min ?? undefined;
      this.filter.extraInfoFilter.nbr_locations_max = this.data.filter?.nbr_locations_max ?? undefined;
      this.filter.extraInfoFilter.nbr_catalogs_min = this.data.filter?.nbr_catalogs_min ?? undefined;
      this.filter.extraInfoFilter.nbr_catalogs_max = this.data.filter?.nbr_catalogs_max ?? undefined;
    } else {
      sub1 = this.currentFilter.subscribe((filter: IAgentFilter) => {
        this.filter = refillAgentFilter(filter);
      });
    }
    this.store.dispatch(FetchAllCountries());
    this.countries$ = this.store.select((state) => state.customers.allCountries);
    const subCountries = this.countries$.subscribe((countries) => {
      this.countries = countries;
    })
    this.subscriptions.push(subCountries)
    if (sub1) {
      this.subscriptions.push(sub1);
    }
  }

  addTag(event: MatChipInputEvent): void {
    const input = (event.value || '').trim();
    if (input && !this.filter.extraInfoFilter.tags.some(tag => tag === input)) {
      this.filter.extraInfoFilter.tags.push(input);
    }
    event.chipInput?.clear();
  }

  removeTag(tagToRemove: string): void {
    this.filter.extraInfoFilter.tags = this.filter.extraInfoFilter.tags.filter(tag => tag !== tagToRemove);
  }

  getTags(): string {
    return (this.filter?.extraInfoFilter?.tags || [])
      .join(', ');
  }

  getFiltersCount(): number {
    return parseAgentFilterRequest(this.filter)?.filters?.length || 0;
  }

  close(): void {
    this.dialogRef.close();
  }

  clearFilters(): void {
    this.filter = refillAgentFilter();
    this.filterName = '';
  }

  toggleSaveFilter(): void {
    this.toggleSaveFilterForm = !this.toggleSaveFilterForm;
  }

  save(): boolean | void {
    if (!this.filterNameControl.valid) {
      this.filterNameControl.markAsTouched();
      return false;
    }
    if (!this.agentFilters?.filters)
      this.agentFilters = {
        filters: [{ name: this.filterName, filter: this.filter }],
      };
    else {
      const filter = this.agentFilters.filters?.find((f: any) => f.name.toLowerCase() === this.filterName.toLowerCase());
      if (filter)
        this.agentFilters.filters = this.agentFilters.filters?.map((f: any) => {
          if (f.name === this.filterName) return { name: this.filterName, filter: this.filter };
          return f;
        });
      else
        this.agentFilters.filters.push({
          name: this.filterName,
          filter: this.filter,
        });
    }
    LocalStorageService.put('agentFilters', this.agentFilters);
    this.toggleSaveFilter();
  }

  setFilter(filter: any): void {
    if (filter) {
      this.filterName = filter.name;
      this.filter = refillAgentFilter(filter.filter);
    }
  }

  deleteFilter(filter: any): void {
    this.agentFilters.filters = this.agentFilters.filters?.filter((f: any) => f.name !== filter.name);
    LocalStorageService.put('agentFilters', this.agentFilters);
  }

  apply(): void {
    this.dialogRef.close(this.filter);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
